import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
	IconButton,
	Typography,
	CircularProgress,
	Box,
	Chip,
	Button
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import SyncIcon from '@material-ui/icons/Sync';
import { makeStyles } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

const useStyles = makeStyles((theme) => ({
	dialogTitle: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center'
	},
	statusItem: {
		display: 'flex',
		justifyContent: 'space-between',
		marginBottom: theme.spacing(1),
		padding: theme.spacing(1),
		backgroundColor: theme.palette.background.default,
		borderRadius: theme.shape.borderRadius
	},
	successIcon: {
		color: theme.palette.success.main,
		marginRight: theme.spacing(1)
	},
	errorIcon: {
		color: theme.palette.error.main,
		marginRight: theme.spacing(1)
	},
	loadingBox: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: theme.spacing(3)
	},
	resultSection: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2)
	},
	sourceChip: {
		textTransform: 'uppercase',
		fontWeight: 500
	},
	datePickerBox: {
		display: 'flex',
		flexDirection: 'column',
		padding: 0
	},
	dialogContent: {
		paddingTop: theme.spacing(1.5),
		paddingBottom: theme.spacing(1.5)
	},
	dialogTitleRoot: {
		padding: theme.spacing(1.5, 2)
	},
	closeButton: {
		padding: theme.spacing(0.5)
	}
}));

function SyncLeadsResultsDialog({ open, onClose, loading, error, syncResults, onSync }) {
	const classes = useStyles();
	const [selectedDate, setSelectedDate] = useState(null);

	useEffect(() => {
		if (open && !loading && !error && !syncResults) {
			setSelectedDate(null);
		}
	}, [open, loading, error, syncResults]);

	const showDatePicker = open && !loading && !error && !syncResults;

	const renderContent = () => {
		if (loading) {
			return (
				<Box className={classes.loadingBox}>
					<CircularProgress />
					<Typography variant="body1" style={{ marginTop: 16 }}>
						Syncing leads from integrations...
					</Typography>
				</Box>
			);
		}

		if (error) {
			return (
				<Box className={classes.loadingBox}>
					<ErrorIcon className={classes.errorIcon} style={{ fontSize: 48 }} />
					<Typography variant="h6" color="error" gutterBottom>
						Sync Failed
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{error}
					</Typography>
				</Box>
			);
		}

		if (showDatePicker) {
			return (
				<Box className={classes.datePickerBox}>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Choose a start date to sync leads from.
					</Typography>
					<MuiPickersUtilsProvider utils={DateFnsUtils}>
						<KeyboardDatePicker
							fullWidth
							margin="dense"
							id="sync-start-date-picker"
							label="Start sync date"
							format="dd/MM/yyyy"
							value={selectedDate}
							onChange={setSelectedDate}
							disableFuture
							KeyboardButtonProps={{
								'aria-label': 'change start sync date'
							}}
						/>
					</MuiPickersUtilsProvider>
				</Box>
			);
		}

		if (!syncResults) {
			return null;
		}

		const { results } = syncResults;

		return (
			<>
				<Box display="flex" alignItems="center" mb={3}>
					<CheckCircleIcon className={classes.successIcon} />
					<Typography variant="h6">Sync Completed Successfully</Typography>
				</Box>

				{results && results.length > 0 && (
					<>
						<Typography variant="subtitle1" gutterBottom>
							Integration Details:
						</Typography>
						{results.map((result, index) => (
							<Box key={index} className={classes.resultSection}>
								<Box display="flex" alignItems="center" mb={1}>
									<Chip
										label={result.source}
										size="small"
										className={classes.sourceChip}
										color={result.status === 'success' ? 'primary' : 'default'}
										icon={result.status === 'success' ? <CheckCircleIcon /> : <SyncIcon />}
									/>
									{result.hasSync && (
										<Typography variant="caption" style={{ marginLeft: 8, color: '#666' }}>
											Last sync: {new Date(result.lastSyncDate).toLocaleString()}
										</Typography>
									)}
								</Box>

								<Box className={classes.statusItem}>
									<Typography variant="body2">Processed:</Typography>
									<Typography variant="body2" style={{ fontWeight: 500 }}>
										{result.processed || 0}
									</Typography>
								</Box>
								<Box className={classes.statusItem}>
									<Typography variant="body2">Created:</Typography>
									<Typography variant="body2" style={{ fontWeight: 500, color: '#4caf50' }}>
										{result.created || 0}
									</Typography>
								</Box>
								{result.createdEmails?.length > 0 && (
									<Box className={classes.statusItem} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
										<Typography variant="body2" style={{ marginBottom: 4 }}>
											Created emails:
										</Typography>
										{result.createdEmails.map((email) => (
											<Typography
												key={email}
												variant="caption"
												style={{ color: '#4caf50', display: 'block' }}
											>
												{email}
											</Typography>
										))}
									</Box>
								)}
								<Box className={classes.statusItem}>
									<Typography variant="body2">Updated:</Typography>
									<Typography variant="body2" style={{ fontWeight: 500, color: '#2196f3' }}>
										{result.updated || 0}
									</Typography>
								</Box>
								<Box className={classes.statusItem}>
									<Typography variant="body2">Errors:</Typography>
									<Typography variant="body2" style={{ fontWeight: 500, color: '#f44336' }}>
										{result.errors || 0}
									</Typography>
								</Box>
								<Box className={classes.statusItem}>
									<Typography variant="body2">Duration:</Typography>
									<Typography variant="body2" style={{ fontWeight: 500 }}>
										{result.duration ? `${result.duration.toFixed(2)}s` : 'N/A'}
									</Typography>
								</Box>
								{result.lastSyncStartTime && result.lastSyncEndTime && (
									<>
										<Box className={classes.statusItem}>
											<Typography variant="body2">Start Time:</Typography>
											<Typography variant="caption">
												{new Date(result.lastSyncStartTime).toLocaleString()}
											</Typography>
										</Box>
										<Box className={classes.statusItem}>
											<Typography variant="body2">End Time:</Typography>
											<Typography variant="caption">
												{new Date(result.lastSyncEndTime).toLocaleString()}
											</Typography>
										</Box>
									</>
								)}
							</Box>
						))}
					</>
				)}
			</>
		);
	};

	return (
		<Dialog maxWidth="xs" fullWidth onClose={onClose} open={open}>
			<DialogTitle disableTypography className={classes.dialogTitleRoot}>
				<div className={classes.dialogTitle}>
					<Typography variant="h6">
						{showDatePicker ? 'Sync Leads' : 'Sync Leads Results'}
					</Typography>
					<IconButton className={classes.closeButton} onClick={onClose}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</div>
			</DialogTitle>
			<DialogContent dividers className={classes.dialogContent}>
				{renderContent()}
			</DialogContent>
			{showDatePicker && (
				<DialogActions>
					<Button
						onClick={() => onSync(selectedDate)}
						color="primary"
						variant="contained"
						size="small"
						disabled={!selectedDate}
					>
						Sync
					</Button>
				</DialogActions>
			)}
		</Dialog>
	);
}

SyncLeadsResultsDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onSync: PropTypes.func.isRequired,
	loading: PropTypes.bool,
	error: PropTypes.string,
	syncResults: PropTypes.shape({
		success: PropTypes.bool,
		summary: PropTypes.shape({
			totalProcessed: PropTypes.number,
			totalCreated: PropTypes.number,
			totalUpdated: PropTypes.number,
			totalErrors: PropTypes.number,
			integrationsRun: PropTypes.number
		}),
		results: PropTypes.arrayOf(
			PropTypes.shape({
				source: PropTypes.string,
				hasSync: PropTypes.bool,
				lastSyncDate: PropTypes.string,
				lastSyncStartTime: PropTypes.string,
				lastSyncEndTime: PropTypes.string,
				status: PropTypes.string,
				processed: PropTypes.number,
				created: PropTypes.number,
				updated: PropTypes.number,
				errors: PropTypes.number,
				duration: PropTypes.number,
				createdEmails: PropTypes.arrayOf(PropTypes.string)
			})
		)
	})
};

export default SyncLeadsResultsDialog;
